import React from 'react'
import ReactDom from 'react-dom/server'
import { MARKS, INLINES, BLOCKS } from '@contentful/rich-text-types'
import Gallery from '../components/common/Gallery'
import ImageSmartWidget from '../components/widgets/ImageSmartWidget'
import ScrollableAnchor from 'react-scrollable-anchor'
import get from 'lodash/get'
import map from 'lodash/map'
import { buildURL, CONTENTFUL_ENTRY_TYPES } from './url'
import assets from '../assets/assets.json'
import { isArray, isObject } from 'lodash'

const locale = 'en-NZ'

export const createRichTextOptions = (richtextReferences, richTextOptions, isGuideline) => {
    return {
        renderNode: {
            [INLINES.ASSET_HYPERLINK]: (node, children) => renderAssetHyperlink(node, children, richtextReferences),
            [INLINES.ENTRY_HYPERLINK]: (node, children) => renderEntryHyperlink(node, children, richtextReferences),
            [INLINES.EMBEDDED_ENTRY]: (node, children) => renderEntryByType(node, children, richtextReferences, isGuideline),
            [INLINES.HYPERLINK]: (node, children) => renderHyperlink(node, children, richtextReferences),
            [BLOCKS.EMBEDDED_ENTRY]: (node, children) => renderEntryByType(node, children, richtextReferences, isGuideline),
            [BLOCKS.EMBEDDED_ASSET]: (node, children) => renderAssetByType(node, children, richtextReferences),
            [BLOCKS.PARAGRAPH]: renderParagraph,
            [BLOCKS.HEADING_3]: renderH3,
            [BLOCKS.TABLE]: renderBlockTable,
            ...richTextOptions?.renderNode,
        },
        renderMark: {
            [MARKS.BOLD]: (text) => <strong>{text}</strong>,
            [MARKS.SUBSCRIPT]: (text) => <sub>{text}</sub>,
            [MARKS.SUPERSCRIPT]: (text) => <sup>{text}</sup>,
            ...richTextOptions?.renderMark
        },
    }
}

export default {
    renderNode: {
        [INLINES.ASSET_HYPERLINK]: defaultRenderAssetHyperlink ,
        [INLINES.ENTRY_HYPERLINK]: defaultRenderEntryHyperLink,
        [INLINES.EMBEDDED_ENTRY]: defaultRenderEntryByType,
        [INLINES.HYPERLINK]: renderHyperlink,
        [BLOCKS.EMBEDDED_ENTRY]: defaultRenderEntryByType,
        [BLOCKS.EMBEDDED_ASSET]: defaultRenderAssetByType,
        [BLOCKS.PARAGRAPH]: renderParagraph,
        [BLOCKS.HEADING_3]: renderH3,
        [BLOCKS.TABLE]: renderBlockTable
    },
    /*
     * Defines custom html string for each mark type like bold, italic etc..
     */
    renderMark: {
        [MARKS.BOLD]: (text) => <strong>{text}</strong>,
        [MARKS.SUBSCRIPT]: (text) => <sub>{text}</sub>,
        [MARKS.SUPERSCRIPT]: (text) => <sup>{text}</sup>,
    },
}

function errorRendering(node, typeId) {
    return <div>Error with Entry. This could mean the Entry you're trying to access it's not available anymore. Please review it in the CMS: ${JSON.stringify(node)} - Type ID: {typeId}</div>
}

function renderEntryByType(node, children, richtextReferences, isGuideline) {
    const typeId = get(node, 'data.target.sys.contentType.sys.id')
    const targetType = get(node, 'data.target.sys.type')

    if (!typeId && targetType === 'Link') {
        return renderEntryFromReferences(node, children, richtextReferences, isGuideline)
    }
    return defaultRenderEntryByType(node, children, false, isGuideline)
}

function defaultRenderEntryByType(node, children, withReferences, isGuideline) {
    const typeId = get(node, 'data.target.sys.contentType.sys.id')
    try {
        
        if (!typeId) {
            return errorRendering(node, typeId)
        }

        if (typeId === 'alert') {
            return renderAlert(node, children, withReferences)
        }

        if (typeId === 'table') {
            return renderTable(node, children, withReferences)
        }

        if (typeId === 'anchor') {
            return renderAnchor(node, children)
        }

        if (typeId === 'widgetImage' || typeId === 'widgetImagePTP') {
            return renderWidgetImage(node, children, withReferences, isGuideline)
        }

        if (typeId === 'widgetEmbedded' || typeId === 'widgetEmbeddedPTP') {
            return renderWidgetEmbed(node, children, withReferences)
        }

        return renderGenericEntry(node, children)
    } catch (error) {
        console.warn(`Error during rendering in rixhtext react node ${JSON.stringify(node)} : ${error}`)
        return errorRendering(node, typeId)
    }
}

function renderEntryFromReferences(node, children, richtextReferences, isGuideline) {
    const entryId = get(node, 'data.target.sys.id')
    const entry = richtextReferences?.find((ref) => get(ref, 'contentful_id') === entryId)
    const entrySys = get(entry, 'sys', {})
    const nodeEntry = { data: { target: { sys: entrySys, fields: entry } } }

    return defaultRenderEntryByType(nodeEntry, children, true, isGuideline)
}

/**
 * This method returns the value of a field. If it has a property with the same value as locale it returns that or else just the field.
 * @param {Object} node
 * @param {String} fieldPath - Path to the field. e.g.: fields.title
 */
function getField(node, fieldPath) {
    const field = get(node, fieldPath)
    return get(field, locale, field)
}

function renderH3(node, children) {
    let value = children.join('')
    if (value.includes('[object Object]')) {
        value = map(children, (child, i) => {
            if (isObject(child)) {
                return isArray(get(child, 'props.children')) ? get(child, 'props.children').join('') : get(child, 'props.children')
            }
            return child
        })
        value = value.join('')
    }
    const name = value
        ? value
              .trim()
              .replace(/[^\w\s\d-]/gi, '')
              .replace(/\s+/g, '-')
              .toLowerCase()
        : ''
    return (
        <h3 id={name} name={name}>
            {children}
        </h3>
    )
}

function renderAnchor(node) {
    const title = getField(node, 'data.target.fields.title')
    const slug = getField(node, 'data.target.fields.slug')
    return (
        <ScrollableAnchor id={`${slug}`}>
            <div>{title}</div>
        </ScrollableAnchor>
    )
}

function renderGallery(node, isGuideline) {
    const images = getField(node, 'data.target.fields.images')
    const items = map(images, (image) => {
        const file = get(image, 'file') || getField(image, 'fields.file')
        const title = get(image, 'title') || getField(image, 'fields.title')
        const description = get(image, 'description') || getField(image, 'fields.description')

        return { alt: description, title, src: file.url }
    })

    const position = getField(node, 'data.target.fields.position')
    const wrapText = getField(node, 'data.target.fields.wrapText')
    const title = getField(node, 'data.target.fields.imageTitle') || getField(node, 'data.target.fields.title') || ''
    const slug = getField(node, 'data.target.fields.slug')
    const data = { id: slug, position, title, images: items, wrapText }

    if (isGuideline) {
        return <div id={`${slug}`} className="gallery" data-wrap-text={wrapText} data-position={position}>
            <div className="gallery-caption">
                {title}
            </div>
            {items.map((item, i) => (
                <div className="item" key={`item-${i}`}>
                    <img oncontextmenu="return false;" src={`https:${item.src}`} alt={item.alt} title={item.title} />
                    <div className="item-caption">{item.alt}</div>
                </div>
            ))}
        </div>
    }

    return <Gallery data={data} key={slug} />
}

function renderAlert(node, children, withReferences) {
    const text = withReferences ? getField(node, 'data.target.fields.text.text') : getField(node, 'data.target.fields.text')
    return <div className="alert">{text}</div>
}

function renderBlockTable(node, children) {
    return (
        <div className='table table-responsive'>
            <table border="1" cellPadding="0" cellSpacing="0" >
                <thead>{children[0]}</thead>
                <tbody>{children.slice(1)}</tbody>
            </table> 
        </div>
    )
}
function renderTable(node, children, withReferences = false) {
    const contentFieldName = withReferences ? 'test.test' : 'test'
    const value = getField(node, `data.target.fields.${contentFieldName}`)
    const isDosageTable = getField(node, 'data.target.fields.isDosageTable') || false
    const tableName = withReferences ? getField(node, 'data.target.fields.title.title') : getField(node, 'data.target.fields.title')
    const slug = getField(node, 'data.target.fields.slug')

    // Removing styles from table td
    const tableUpdated = value.replace(/style="[^"]*"/g, '').replace(/<a/g, `<a target="_blank"`)
    return <div dangerouslySetInnerHTML={{ __html: `<div class='custom-entry table-responsive ${isDosageTable ? 'dosage-table' : ''}' data-name='${tableName}' id='${slug}'>${tableUpdated}</div>` }} />
}

/**
 * When we recieve an entry that we do not recognise we show a warning in the console and try to render something so developers would get a hint on what's wrong.
 * @param {Object} node
 * @returns {String} html string
 */
function renderGenericEntry(node) {
    console.warn(`\nThere's no render method for generic entry of type ${node.data.target.sys.contentType.sys.id}`)
    const fields = node.data.target.fields
    const field = fields.name || fields.title || fields[0] // We are trying to guess an existing field to show something.
    const value = !field ? 'empty' : field[locale] || field

    return (
        <div className="unknown-entry">
            <p>Please implement a render method to display the entry: {value}</p>
        </div>
    )
}

function getAssetType(node) {
    const fields = getField(node, 'data.target.fields')
    if (fields && fields.file) {
        const file = fields.file[locale] || fields.file
        return file.contentType
    }
    return null
}

/**
 * This method will render assets depending if it's an image or a link to download an asset (pdf/zip/etc) or if it doesn't recognize the asset it will write a warning in the terminal and render the asset as a link.
 * @param {Object} node
 * @param {Function} content
 * @returns {String} html string
 */
function renderAssetByType(node, content, richtextReferences) {
    const targetType = get(node, 'data.target.sys.type')
    const linkType = get(node, 'data.target.sys.linkType')
    if (targetType === 'Link' && linkType === 'Asset') {
        return renderAssetWithReference(node, content, richtextReferences)
    }
    return defaultRenderAssetByType(node, content)
}

function defaultRenderAssetByType(node, content) { 
    const type = getAssetType(node) || 'image/jpeg'

    if (/^image\/.+/i.exec(type)) {
        return renderImage(node)
    }

    return renderGenericAsset(node, content)
}

function renderAssetWithReference(node, content, references) {
    const assetId = get(node, 'data.target.sys.id')
    const asset = references?.find((ref) => get(ref, 'contentful_id') === assetId)
    const assetSys = get(asset, 'sys', {})
    const nodeEntry = { data: { target: { sys: { ...assetSys, contentful_id: assetId }, fields: asset } } }

    return defaultRenderAssetByType(nodeEntry, content)
 }

function renderImage(node) {
    const fields = get(node, 'data.target.fields')
    const file = get(fields, `file[${locale}]`, fields?.file)
    try {
        if (fields && file) {
            const src = file.url
            const title = get(fields, 'assetTitle') || getField(fields, 'title')
            return (
                <div id={`asset-${getSlug(title)}`} className="image-container text-center">
                    <img src={src} alt={title} title={title} onContextMenu={(e) => e.preventDefault()} />
                </div>
            )
        } else return errorRendering(node, 'renderImage')
    } catch (error) {
        console.warn(`Error when rendering node ${JSON.stringify(node)} : ${error}`)
        return errorRendering(node, 'renderImage')
    }
}

/**
 * This method shows a warning for a component that we don't support.
 * @param {Object} node
 * @returns {String} html string
 */
function renderGenericAsset(node, children) {
    const fields = get(node, 'data.target.fields')
    const file = get(fields, `file[${locale}]`, fields.file)
    console.warn(`\nThere's no render method for generic asset of type ${file.contentType}`)

    return renderHyperlink(node, children)
}

/**
 * This method will render assets as anchor hyperlinks if we regonize them as such (pdf, zip, etc) or will create local links prefixed with "asset-". For example a link to an image/table that exists in the same page.
 * @param {Object} node
 * @param {Function} children
 * @returns {String} html string
 */
function renderAssetHyperlink(node, children, richtextReferences) {
    const targetType = get(node, 'data.target.sys.type')
    const linkType = get(node, 'data.target.sys.linkType')
    if (targetType === 'Link' && linkType === 'Asset') { 
        return renderAssetHyperLinkWithReference(node, children, richtextReferences)
    }

    return defaultRenderAssetHyperlink(node, children)
    
}

function defaultRenderAssetHyperlink(node, children) {
    const type = getAssetType(node)
    if (!type) {
        console.warn(`Error when rendering node ${JSON.stringify(node)}`)
        return errorRendering(node, 'renderAssetHyperlink')
    }
    if (/^image\/.+/i.exec(type)) {
        const assetTitle = getField(node, 'data.target.fields.title') || ''
        const title = children.length ? children : assetTitle
        return (
            <a href={`#${getSlug(assetTitle, 'asset')}`} className="asset-link">
                {title}
            </a>
        )
    }

    return renderHyperlink(node, children)
}

function renderAssetHyperLinkWithReference(node, children, richtextReferences) { 
    const assetId = get(node, 'data.target.sys.id')
    const asset = richtextReferences?.find((ref) => get(ref, 'contentful_id') === assetId)
    const assetSys = get(asset, 'sys', {})
    const nodeEntry = { data: { target: { sys: { ...assetSys, contentful_id: assetId }, fields: asset } } }

    return defaultRenderAssetHyperlink(nodeEntry, children)
}

function renderEntryHyperlink(node, children, richtextReferences) {
    const targetType = get(node, 'data.target.sys.type')
    const linkType = get(node, 'data.target.sys.linkType')
    if (targetType === 'Link' && linkType === 'Entry') {
        return renderEntryLinkWithReference(node, children, richtextReferences)
    }
    return defaultRenderEntryHyperLink(node, children)
}

function defaultRenderEntryHyperLink(node, children) {
    const type = get(node, 'data.target.sys.contentType.sys.id')
    const slug = getField(node, 'data.target.fields.slug')
    const text = children.length ? children : getField(node, 'data.target.fields.title')

    const isExternalReference = type && Object.values(CONTENTFUL_ENTRY_TYPES).includes(type)
    if (isExternalReference) {
        const url = buildURL({
            slug: slug,
            userSection: getField(node, 'data.target.fields.userSection'),
            sys: node.data.target.sys,
        })
        return (
            <a href={url} target="_blank" className="entry-link">
                {text}
            </a>
        )
    }

    const url = slug ? `#${slug}` : getField(node, 'data.target.fields.url')
    const isAnchor = url ? url.includes('#') : false
    const targetAttr = isAnchor ? '' : '_blank'
    return (
        <a href={url} target={targetAttr} className="entry-link">
            {text}
        </a>
    )
}

function renderEntryLinkWithReference(node, children, richtextReferences) {
    const entryId = get(node, 'data.target.sys.id')
    const entry = richtextReferences?.find((ref) => get(ref, 'contentful_id') === entryId)
    const entrySys = get(entry, 'sys', {})
    const nodeEntry = {
        data: {
            target: {
                sys: entrySys,
                fields: {
                    ...entry,
                    title: get(entry, 'guidelineTitle') || get(entry, 'genericTitle') || get(entry, 'groupTitle') || get(entry, 'widgetTitle') || get(entry, 'blogTitle') || get(entry, 'title') || ''
                }
            }
        }
    }
    return defaultRenderEntryHyperLink(nodeEntry, children)
}

function shouldOpenInNewTab(uri) {
    return !/^mailto:.*/.test(uri) && !/^tel:.*/.test(uri)
}

function renderHyperlink(node, children) {
    let uri = getField(node, 'data.uri')
    if (!uri) {
        const file = getField(node, 'data.target.fields.file')
        if (file) {
            uri = file.url
        }
    }

    const content = children.length ? children : get(node, 'data.target.fields.assetTitle') || getField(node, 'data.target.fields.title')

    if (!uri) {
        uri = ''
    }
    const preview = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('preview')
    const assetId = getField(node, 'data.target.sys.contentful_id')
    const shortURL = assets[assetId]
    if (shortURL && (!preview || preview === 'false')) {
        uri = shortURL
    }

    // New emails open in the same tab STAR-284
    // const targetAttr = shouldOpenInNewTab(uri) ? "_blank" : "";
    const isAnchor = uri ? uri.includes('#') : false
    const targetAttr = isAnchor ? '' : '_blank'
    return (
        <a href={uri} target={targetAttr}>
            {content}
        </a>
    )
}

function getSlug(title, prefix) {
    const slug = title
        .replace(/[^\w\s\d-]/gi, '')
        .replace(/\s+/g, '-')
        .toLowerCase()

    return prefix ? `${prefix}-${slug}` : slug
}

function renderParagraph(node, children) {
    const paragraph = ReactDom.renderToString(children)
    if (paragraph === '') return <p>&nbsp;</p>

    return <p dangerouslySetInnerHTML={{ __html: paragraph.replace(/\n/g, '<br>') }} />
}

function renderWidgetImage(node, children, withReferences, isGuideline) {
    const images = getField(node, 'data.target.fields.images')
    if (!images) return <div></div>
    if (images.length > 1) {
        return renderGallery(node, isGuideline)
    }
    const file = withReferences ? get(images[0], 'file') : getField(images[0], 'fields.file')
    if (file && file.contentType.includes('image/')) {
        const enableHyperlink = getField(node, 'data.target.fields.enableHyperlink')
        let hyperlink = getField(node, 'data.target.fields.hyperlink')
        // Contentful aliases the widget image title differently depending on the template:
        // - WidgetsPageTemplate.js: widgetImageTitle: title, imageTitle: title
        // So we read those first to avoid ending up with `undefined`.
        const title =
            getField(node, 'data.target.fields.imageTitle') ||
            getField(node, 'data.target.fields.widgetImageTitle') ||
            getField(node, 'data.target.fields.title') ||
            ''
        const position = getField(node.data.target.fields, 'position')
        let width = !node.data.target.fields.width ? 100 : node.data.target.fields.width[locale] ? node.data.target.fields.width[locale] : node.data.target.fields.width
        width = width > 100 ? 100 : width
        const wrapText = getField(node, 'data.target.fields.wrapText')
        const hyperlinkEntryArray = getField(node, 'data.target.fields.hyperlinkEntry')
        if (hyperlinkEntryArray) {
            const hyperlinkEntry = hyperlinkEntryArray[0] ? hyperlinkEntryArray[0] : hyperlinkEntryArray
            if (hyperlinkEntry.sys.contentType.sys.id === 'media') {
                const media = getField(hyperlinkEntry, 'media') || getField(hyperlinkEntry, 'fields.media')
                const assetId =  getField(media, 'contentful_id') || getField(media, 'sys.contentful_id')
                const assetFile = getField(media, 'file') || getField(media, 'fields.file')
                const assetURL = getField(assetFile, 'url')
                const shortURL = assets[assetId] || assetURL
                hyperlink = shortURL
            } else {
                hyperlink = buildURL({ ...hyperlinkEntry })
            }
        }

        const data = { file, wrapText, enableHyperlink, hyperlink, title, position: position ? position.toLowerCase() : 'left', width }
        return <ImageSmartWidget data={data} />
    }

    return renderGenericEntry(node)
}
function renderWidgetEmbed(node, children, withReferences) {
    const fields = get(node, 'data.target.fields')
    const title = withReferences ? get(fields, 'title.title') || get(fields, 'title') : get(fields, `title[${locale}]`, fields.title)
    const type = withReferences ?  get(fields, 'type.type') || get(fields, 'type') : get(fields, `type[${locale}]`, fields.type)
    const urlOrCode = withReferences ? get(fields, 'url.url') : get(fields, `url[${locale}]`, fields.url)
    const htmlCode = withReferences ? get(fields, 'htmlCode.file.url') : get(fields, 'htmlCode.fields.file.url', '')
    const htmlUrl = get(fields, `htmlCode[${locale}][fields][file][${locale}][url]`, htmlCode)

    if (type.toLowerCase() === 'url') {
        const url = urlOrCode.replace('youtu.be', 'www.youtube.com/embed').replace('watch?v=', 'embed/').replace('vimeo.com', 'player.vimeo.com/video')

        return (
            <div className="embed-responsive embed-responsive-16by9">
                <iframe title={title} className="embed-responsive-item" src={url} frameBorder="0" allowFullScreen />
            </div>
        )
    }

    if (type.toLowerCase() === 'embed file') {
        return <iframe title={title} type="text/html" data-embed-id={title.replace(/ /g, '_')} data-embed-url={htmlUrl} id="custom-embed" className="w-100" frameborder="0" allowfullscreen></iframe>
    }
    const regexp = RegExp('src="(.*?)"', 'g')

    const src = regexp.exec(urlOrCode)

    if (src == null || src[1] == null) {
        return 'Please check ScreenCast embed code'
    }

    return (
        <div className="embed-responsive embed-responsive-16by9">
            <iframe title={title} className="embed-responsive-item" src={src[1]} frameBorder="0" allowFullScreen />
        </div>
    )
}
